export type TaskRouteMode = "board" | "backlog" | "scrum";
export type PortalView = "retros" | "actions" | "board" | "backlog" | "hierarchy" | "sprints" | "goals" | "reports" | "scrum" | "imports" | "onboarding" | "admin";

export type PortalRoute = {
  retrospectiveId: string | null;
  sprintId: string | null;
  taskId: string | null;
  taskMode: TaskRouteMode;
  teamId: string | null;
  view: PortalView;
  query?: string;
};

const safelyDecode = (value: string | undefined) => {
  if (!value) return null;
  try { return decodeURIComponent(value); } catch { return null; }
};

export const readPortalRoute = (
  pathname = window.location.pathname,
  search = window.location.search,
): PortalRoute => {
  const parts = pathname.split("/").filter(Boolean);
  const route: PortalRoute = {
    retrospectiveId: null,
    sprintId: null,
    taskId: null,
    taskMode: "board",
    teamId: null,
    view: new URLSearchParams(search).has("jira") ? "imports" : "retros",
  };
  const parameters = new URLSearchParams(search);
  route.query = parameters.get("q") ?? "";
  if (parts[0] !== "teams") return route;
  route.teamId = safelyDecode(parts[1]);
  const resource = parts[2];
  if (resource === "retrospectives") {
    route.retrospectiveId = safelyDecode(parts[3]);
  } else if (resource === "actions") {
    route.view = "actions";
  } else if (resource === "admin") {
    route.view = "admin";
  } else if (resource === "imports") {
    route.view = "imports";
  } else if (resource === "onboarding") {
    route.view = "onboarding";
  } else if (resource === "backlog") {
    route.view = "backlog";
    route.taskMode = "backlog";
  } else if (resource === "daily-scrum") {
    route.view = "scrum";
    route.taskMode = "scrum";
  } else if (resource === "hierarchy") {
    route.view = "hierarchy";
  } else if (resource === "sprints") {
    route.view = "sprints";
    route.sprintId = safelyDecode(parts[3]);
  } else if (resource === "goals") {
    route.view = "goals";
  } else if (resource === "reports") {
    route.view = "reports";
    route.sprintId = safelyDecode(parts[3]);
  } else if (resource === "tasks") {
    route.view = "board";
    route.taskId = safelyDecode(parts[3]);
    const parameters = new URLSearchParams(search);
    route.sprintId = parameters.get("sprint");
    const sourceView = parameters.get("view");
    if (sourceView === "backlog" || sourceView === "scrum") {
      route.taskMode = sourceView;
      route.view = sourceView;
    }
  } else if (resource === "board") {
    route.view = "board";
    route.sprintId = new URLSearchParams(search).get("sprint");
  }
  return route;
};

// A shareable link is /t/:taskKey -- the human-readable "PROJ-123" number,
// not the internal task UUID the rest of the app routes on. It's team-
// agnostic (task_key is globally unique, see migrations/0009), so resolving
// one means finding which team it belongs to before the normal team-scoped
// UI above can render it -- see SharedTaskRedirect in main.tsx.
export const readSharedTaskKey = (pathname = window.location.pathname): string | null => {
  const parts = pathname.split("/").filter(Boolean);
  return parts[0] === "t" && parts[1] ? safelyDecode(parts[1]) : null;
};

export const portalRoutePath = (route: PortalRoute) => {
  if (!route.teamId) return "/";
  const team = `/teams/${encodeURIComponent(route.teamId)}`;
  if (route.retrospectiveId) return `${team}/retrospectives/${encodeURIComponent(route.retrospectiveId)}`;
  if (route.view === "actions") return `${team}/actions`;
  if (route.view === "admin") return `${team}/admin`;
  if (route.view === "imports") return `${team}/imports`;
  if (route.view === "onboarding") return `${team}/onboarding`;
  if (["board", "backlog", "scrum"].includes(route.view)) {
    if (route.taskId) {
      const parameters = new URLSearchParams();
      if (route.sprintId) parameters.set("sprint", route.sprintId);
      if (route.taskMode !== "board") parameters.set("view", route.taskMode);
      const search = parameters.size ? `?${parameters}` : "";
      return `${team}/tasks/${encodeURIComponent(route.taskId)}${search}`;
    }
    const query = route.query ? `?q=${encodeURIComponent(route.query)}` : "";
    if (route.view === "backlog") return `${team}/backlog${query}`;
    if (route.view === "scrum") return `${team}/daily-scrum`;
    const boardParameters = new URLSearchParams();
    if (route.sprintId) boardParameters.set("sprint", route.sprintId);
    if (route.query) boardParameters.set("q", route.query);
    return `${team}/board${boardParameters.size ? `?${boardParameters}` : ""}`;
  }
  if (route.view === "hierarchy") return `${team}/hierarchy${route.query ? `?q=${encodeURIComponent(route.query)}` : ""}`;
  if (route.view === "sprints") return `${team}/sprints${route.sprintId ? `/${encodeURIComponent(route.sprintId)}` : ""}`;
  if (route.view === "goals") return `${team}/goals`;
  if (route.view === "reports") return `${team}/reports${route.sprintId ? `/${encodeURIComponent(route.sprintId)}` : ""}`;
  return `${team}/retrospectives`;
};
